/**
 * Wave 14 Agent TRIAL - API client.
 *
 * Bound to /api/v1/wave14/trial/*, /api/v1/wave14/demo/*, and
 * /api/v1/wave14/pilot/*. The legacy /demo and /trial-emails namespaces
 * are owned by earlier waves so we mount under /wave14/* to avoid any
 * collision.
 */
import { api } from '@/lib/api'

export type MilestoneType =
  | 'upload_real_boq'
  | 'add_5_team_members'
  | 'complete_project_setup'
  | 'generate_first_invoice'
  | 'schedule_demo_with_sales'

export interface TrialMilestoneProgress {
  achieved: string[]
  remaining: string[]
  total: number
  achieved_count: number
}

export interface TrialStatus {
  tenant_id: number
  in_trial: boolean
  days_remaining: number
  trial_ends_at: string | null
  plan_tier: string | null
  milestones: TrialMilestoneProgress
  extension_days_granted_total: number
}

export interface TrialMilestoneRow {
  id: number
  milestone_type: MilestoneType | string
  achieved_at: string | null
  extension_days_granted: number
  email_sent: boolean
}

export interface MilestonesResponse {
  achieved: TrialMilestoneRow[]
  progress: TrialMilestoneProgress
  all_types: string[]
}

export interface TrackMilestoneResult {
  milestone_id: number
  milestone_type: string
  newly_achieved: boolean
  extension_days_granted: number
  new_trial_ends_at: string | null
  tenant_id: number
}

export interface PilotApplication {
  id: number
  tenant_id: number
  status: string
  company_size: string | null
  primary_use_case: string | null
  expected_users: number | null
  csm_assigned_user_id: number | null
  applied_at: string | null
  approved_at: string | null
  started_at: string | null
  ended_at: string | null
  converted_at: string | null
  refunded_at: string | null
}

export interface PilotStatus {
  application: PilotApplication | null
  in_refund_window?: boolean
  days_remaining?: number
  pilot_fee_aed?: number
}

export interface PaymentIntentResponse {
  payment_id: number
  payment_intent_id: string | null
  status: string
  requires_action: boolean
  client_secret: string | null
  amount_aed: number
}

export const wave14Trial = {
  // -- Layer 2: trial engagement
  status: async () => (await api.get<TrialStatus>('/wave14/trial/status')).data,
  milestones: async () =>
    (await api.get<MilestonesResponse>('/wave14/trial/milestones')).data,
  track: async (milestone_type: MilestoneType, metadata?: string) =>
    (
      await api.post<TrackMilestoneResult>('/wave14/trial/milestones/track', {
        milestone_type,
        metadata,
      })
    ).data,
  listExtensions: async () =>
    (await api.get('/wave14/trial/extensions')).data,
  grantExtension: async (params: {
    days: number
    reason?: 'manual_admin_grant' | 'goodwill'
    note?: string
    target_tenant_id?: number
  }) => (await api.post('/wave14/trial/extensions', params)).data,
  dispatchAchievementEmails: async () =>
    (await api.post('/wave14/trial/achievements/dispatch')).data,
}

export const wave14Demo = {
  list: async (params?: { status?: string; limit?: number }) =>
    (await api.get('/wave14/demo/bookings', { params })).data,
  get: async (id: number) =>
    (await api.get(`/wave14/demo/bookings/${id}`)).data,
  setOutcome: async (
    id: number,
    body: {
      outcome:
        | 'interested'
        | 'trial_started'
        | 'pilot_applied'
        | 'not_a_fit'
        | 'follow_up_later'
      outcome_notes?: string
      mark_completed?: boolean
      mark_no_show?: boolean
    }
  ) => (await api.post(`/wave14/demo/bookings/${id}/outcome`, body)).data,
  dispatchDue: async () =>
    (await api.post('/wave14/demo/bookings/dispatch-due')).data,
}

export const wave14Pilot = {
  apply: async (body: {
    company_size?: string
    primary_use_case?: string
    expected_users?: number
    notes?: string
    language?: string
  }) => (await api.post('/wave14/pilot/applications', body)).data,
  me: async () => (await api.get<PilotStatus>('/wave14/pilot/applications/me')).data,
  approve: async (id: number) =>
    (await api.post(`/wave14/pilot/applications/${id}/approve`)).data,
  reject: async (id: number) =>
    (await api.post(`/wave14/pilot/applications/${id}/reject`)).data,
  createPaymentIntent: async (id: number) =>
    (
      await api.post<PaymentIntentResponse>(
        `/wave14/pilot/applications/${id}/payment-intent`
      )
    ).data,
  markPaid: async (id: number, payment_intent_id: string, charge_id?: string) =>
    (
      await api.post(`/wave14/pilot/applications/${id}/mark-paid`, {
        payment_intent_id,
        charge_id,
      })
    ).data,
  refund: async (id: number) =>
    (await api.post(`/wave14/pilot/applications/${id}/refund`)).data,
  convert: async (id: number, annual_invoice_id?: string) =>
    (
      await api.post(`/wave14/pilot/applications/${id}/convert-to-annual`, {
        annual_invoice_id,
      })
    ).data,
  status: async (id: number) =>
    (await api.get(`/wave14/pilot/applications/${id}/status`)).data,
}
